import { isChunkGraphAssetPath } from "@/lib/chunk-recovery-contract";

const ASSET_PROBE_RETRY_MS = 750;
const ASSET_PROBE_TIMEOUT_MS = 8_000;

async function probeOnce(assetPath: string): Promise<boolean> {
  const isStyle = assetPath.endsWith(".css");
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), ASSET_PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(assetPath, {
      // Recovery verifies assets already selected and loaded by the new
      // document. Reuse that immutable response when present instead of
      // multiplying network traffic during a failure recovery.
      cache: "force-cache",
      signal: controller.signal,
    });
    const contentType = response.headers.get("content-type") || "";
    if (!response.ok || !contentType.includes(isStyle ? "text/css" : "javascript")) return false;
    await response.arrayBuffer();
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

/** Verify one current immutable JS/CSS asset, with one bounded transient retry. */
export async function probeChunkAsset(assetPath: string): Promise<boolean> {
  if (!isChunkGraphAssetPath(assetPath)) return false;
  if (await probeOnce(assetPath)) return true;
  await new Promise<void>((resolve) => setTimeout(resolve, ASSET_PROBE_RETRY_MS));
  return probeOnce(assetPath);
}

function currentChunkPath(rawUrl: string): string | undefined {
  try {
    const url = new URL(rawUrl, location.href);
    const path = url.pathname;
    return url.origin === location.origin && isChunkGraphAssetPath(path) ? path : undefined;
  } catch {
    return undefined;
  }
}

/** Enumerate the JS/CSS graph declared by the document that replaced the failed one. */
export function currentDocumentChunkPaths(root: ParentNode = document): string[] {
  const paths = new Set<string>();
  const elements = root.querySelectorAll<HTMLScriptElement | HTMLLinkElement>(
    'script[src],link[rel="stylesheet"][href],link[rel="modulepreload"][href],link[rel="preload"][href]'
  );
  for (const element of elements) {
    if (element instanceof HTMLLinkElement && element.rel === "preload") {
      if (element.as !== "script" && element.as !== "style") continue;
    }
    const path = currentChunkPath(
      element instanceof HTMLScriptElement ? element.src : element.href
    );
    if (path) paths.add(path);
  }
  return [...paths];
}

/** Prove the new document's current chunk graph, never the retired asset that caused reload. */
export async function proveCurrentDocumentChunkGraph(
  probeAsset: (assetPath: string) => Promise<boolean> = probeChunkAsset,
  getPaths: () => string[] = currentDocumentChunkPaths
): Promise<boolean> {
  try {
    const paths = getPaths();
    return (
      paths.some((path) => path.endsWith(".js")) &&
      (await Promise.all(paths.map((path) => probeAsset(path)))).every(Boolean)
    );
  } catch {
    return false;
  }
}
